import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthProvider';
import { AuthShell } from '@/components/AuthShell';
import { api, setToken } from '@/lib/api';
import {
  firebaseReloadIdToken,
  firebaseSendVerificationEmail,
  mapFirebaseAuthError,
} from '@/lib/firebaseClient';

/**
 * Shown when a signed-in user has not verified email — blocks the TMS shell.
 */
export default function VerifyEmailPage() {
  const { user, refresh, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleResend = async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      await firebaseSendVerificationEmail();
      setInfo('Verification email sent. Check inbox and spam.');
    } catch (err) {
      setError(mapFirebaseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChecked = async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const idToken = await firebaseReloadIdToken();
      setToken(idToken);
      await api('/api/v1/auth/session', { method: 'POST', token: idToken, body: {} });
      const profile = await refresh();
      if (profile?.user?.emailVerified === false) {
        setError('Still unverified. Open the link in your email, then try again.');
        return;
      }
      setInfo('Email verified — opening your workspace…');
    } catch (err) {
      setError(mapFirebaseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Secure access"
      headline="Confirm your work email before opening the board."
      subhead="We keep dispatch, settlements, and company data behind a verified inbox so open signups cannot walk into a live workspace."
    >
      <div className="space-y-5">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icons.MailCheck className="h-6 w-6" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">Verify your email</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We sent a confirmation link to{' '}
            <span className="font-medium text-foreground">{user?.email || 'your inbox'}</span>.
            Until you confirm it, the dashboard stays locked.
          </p>
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {info ? <p className="text-sm text-primary">{info}</p> : null}

        <div className="flex flex-col gap-2">
          <Button onClick={handleChecked} disabled={loading} className="w-full">
            {loading ? 'Checking…' : "I've verified — continue"}
          </Button>
          <Button variant="outline" onClick={handleResend} disabled={loading} className="w-full">
            Resend verification email
          </Button>
          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={() => void logout()}
            disabled={loading}
          >
            Sign out
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Wrong account?{' '}
          <Link to="/login" className="text-primary hover:underline" onClick={() => void logout()}>
            Use a different email
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
